import type { ReactElement } from 'react';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

export interface ProductRow {
  id: string;
  code: string;
  name: string;
  class: string;
  unit: string;
  defaultSalesAccount: string;
  active: boolean;
}

const COLUMNS: GroupedColumnDef[] = [
  { id: 'code', label: 'Code', header: 'Code' },
  { id: 'name', label: 'Name', header: 'Name' },
  { id: 'class', label: 'Class', header: 'Class' },
  { id: 'unit', label: 'Unit', header: 'Unit' },
  { id: 'account', label: 'Sales account', header: 'Sales account' },
  { id: 'status', label: 'Status', header: 'Status' }
];

function groupValue(row: ProductRow, columnId: string): string {
  switch (columnId) {
    case 'code':
      return row.code;
    case 'name':
      return row.name;
    case 'class':
      return row.class;
    case 'unit':
      return row.unit;
    case 'account':
      return row.defaultSalesAccount;
    case 'status':
      return row.active ? 'Active' : 'Inactive';
    default:
      return '—';
  }
}

interface ProductsGridProps {
  rows: ProductRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function ProductsGrid({
  rows,
  selectedId,
  onSelect,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}: ProductsGridProps): ReactElement {
  return (
    <section className="card requests-grid-card" data-testid="products-grid">
      <div className="row spread requests-grid-toolbar">
        <h2>Products</h2>
      </div>
      <p className="hint">Click a row for the side panel. WTI is a market grade, not a Product.</p>
      <GroupedDataGrid<ProductRow>
        columns={COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={groupValue}
        colSpan={6}
        emptyMessage="No products match this status."
        groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group."
        renderRow={(row) => (
          <tr
            className={selectedId === row.id ? 'selected' : undefined}
            data-testid="product-row"
            onClick={() => onSelect(row.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(row.id);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <td className="mono-id">{row.code}</td>
            <td>{row.name}</td>
            <td>{row.class}</td>
            <td>{row.unit}</td>
            <td>{row.defaultSalesAccount}</td>
            <td>
              <span className="status-pill">{row.active ? 'Active' : 'Inactive'}</span>
            </td>
          </tr>
        )}
      />
      <GridPaginationBar
        idPrefix="products"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
